import { MONDAY_SKU_DATABASE_BOARD_ID } from 'pages/dashboard/MondayIntegration/constants';

export const SHOPIFY_COMPARED_STORE = 'GPNZ';
export const SHOPIFY_READONLY_STORE = 'GPAU';

export const SYSTEMS = ['guide', 'monday', 'cin7', 'shopify', 'kounta'];

export const PLATFORM_SYSTEMS = ['monday', 'cin7', 'shopify', 'kounta'];

export const ISSUE_META = {
  discrepancy: { label: 'Discrepancy', tag: 'error', row: '#fff1f0' },
  missing: { label: 'Missing', tag: 'warning', row: '#fff7e6' },
  misalignment: { label: 'Misalignment', tag: 'success', row: '#f6ffed' },
};

export const SYSTEM_LABELS = {
  guide: 'Guide',
  monday: 'Monday',
  cin7: 'Cin7',
  shopify: 'Shopify',
  kounta: 'Kounta',
};

/**
 * The api client camelizes response keys, so backend `GPNZ` / `GPAU` arrive as `gPNZ` / `gPAU`.
 * Accept both spellings.
 */
export function getStore(obj, store) {
  if (!obj) return null;
  if (obj[store] !== undefined) return obj[store];
  const camel = store.charAt(0).toLowerCase() + store.slice(1);
  return obj[camel] ?? null;
}

export function formatMoney(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  if (n === 0) return '—';
  return n.toFixed(2);
}

/**
 * Editable field slot for one comparison row. cost_driver and matrix_only rows have one slot;
 * kounta_line rows only expose the sell slot (cost is derived/read-only).
 */
export function collectFieldSlots(fr) {
  if (!fr) return [];
  const rowFlag = fr.hasDiscrepancy !== undefined ? fr.hasDiscrepancy : fr.hasMonCinDiscrepancy;
  if (fr.rowKind === 'kounta_line') {
    if (!fr.sell?.field) return [];
    return [{
      role: 'sell',
      field: fr.sell.field,
      label: fr.sell.label,
      guide: fr.sell.guide,
      monday: fr.sell.monday,
      matrix: fr.sell.matrix,
      cin7: fr.sell.cin7,
      shopify: fr.sell.shopify,
      ref: fr.sell.ref,
      refSource: fr.sell.refSource,
      hasDiscrepancy: fr.sell.hasDiscrepancy ?? rowFlag,
    }];
  }
  if (!fr.field) return [];
  return [{
    role: 'single',
    field: fr.field,
    label: fr.label,
    guide: fr.guide,
    monday: fr.monday,
    matrix: fr.matrix,
    cin7: fr.cin7,
    shopify: fr.shopify,
    ref: fr.ref,
    refSource: fr.refSource,
    hasDiscrepancy: rowFlag,
  }];
}

/** Single editable slot for a row, or null for cost-only kounta_line rows. */
export function getEditableSlot(fr) {
  const slots = collectFieldSlots(fr);
  return slots.length > 0 ? slots[0] : null;
}

export function rowHasDiscrepancy(fr) {
  if (!fr) return false;
  if (fr.hasDiscrepancy !== undefined) return fr.hasDiscrepancy === true;
  return fr.hasMonCinDiscrepancy === true;
}

export function collectUniqueFieldKeys(rows) {
  const set = new Set();
  (rows || []).forEach((row) => {
    collectFieldSlots(row).forEach((s) => set.add(s.field));
  });
  return [...set];
}

export function findSlotForField(rows, field) {
  let found = null;
  (rows || []).some((row) => {
    const slot = collectFieldSlots(row).find((s) => s.field === field);
    if (slot) {
      found = slot;
      return true;
    }
    return false;
  });
  return found;
}

/** Short format label for SKU chips, e.g. "24x1 330ml can" / "50L keg". */
export function skuFormatLabel(sku) {
  if (!sku) return '';
  const ct = String(sku.containerType || '').toLowerCase();
  const ut = String(sku.unitType || '').trim();
  const cs = String(sku.caseType || '').trim();
  if (ct === 'keg') {
    const n = parseInt(ut, 10);
    const litres = Number.isNaN(n) || n < 10000 ? 50 : n / 1000;
    return `${litres}L keg`;
  }
  const parts = [];
  if (cs) parts.push(cs);
  if (ut) parts.push(/^\d+$/.test(ut) ? `${ut}ml` : ut);
  if (ct) parts.push(ct);
  if (parts.length === 0) return sku.skuName || String(sku.skuKey);
  return parts.join(' ');
}

export function skuSystemCounts(sku) {
  const gpnz = getStore(sku?.shopify?.byStore, SHOPIFY_COMPARED_STORE);
  return {
    guide: sku?.guide?.discrepancyCount || 0,
    monday: sku?.monday?.discrepancyCount || 0,
    cin7: sku?.cin7?.discrepancyCount || 0,
    shopify: sku?.shopify?.discrepancyCount ?? gpnz?.discrepancyCount ?? 0,
    kounta: sku?.kounta?.discrepancyCount || 0,
  };
}

export function skuIssueCounts(sku) {
  const counts = sku?.issueCounts || {};
  return {
    discrepancy: sku?.totalDiscrepancies ?? counts.discrepancy ?? 0,
    missing: counts.missing || 0,
    misalignment: counts.misalignment || 0,
    kountaCost: counts.kountaCost || 0,
  };
}

/**
 * Live Shopify product (show admin link / picker value).
 * Gap / N/A — not a live product:
 *  - `matchMethod === 'none'` (auto-match may still stash a leftover product id)
 *  - manual unlink (`manual` + null ids)
 *  - format not sold (kegs / unknown; no ids)
 */
export function shopifyLinkIsLive(link) {
  if (!link) return false;
  if (link.matchMethod === 'none') return false;
  return link.shopifyProductId != null || link.shopifyVariantId != null;
}

export function skuSystemLinked(sku) {
  const gpnz = getStore(sku?.shopify?.byStore, SHOPIFY_COMPARED_STORE);
  return {
    guide: Boolean(sku?.guide?.available),
    monday: Boolean(sku?.monday?.linked),
    cin7: Boolean(sku?.cin7?.linked),
    shopify: shopifyLinkIsLive(gpnz),
    kounta: Boolean(sku?.kounta?.linked),
  };
}

const SHOPIFY_FORMAT_NOT_SOLD = 'Format not sold on Shopify';

function blankId(value) {
  return value == null || String(value).trim() === '';
}

/**
 * True when that store has no Shopify product on the link.
 * A product id counts even when matchMethod is `none` (product linked, variant not
 * priced yet) — that is what the card shows. Formats not sold there stay false.
 */
function shopifyStoreLinkMissing(link) {
  if (link?.reason === SHOPIFY_FORMAT_NOT_SOLD) return false;
  return blankId(link?.shopifyProductId);
}

/**
 * Platform link cards with no SKU link.
 * Monday id / Cin7 code present counts as linked even when the price cache is stale.
 * Kegs are optional on Shopify NZ and AU, so those stores are never a gap.
 */
export function skuPlatformLinkMissing(sku) {
  const nz = getStore(sku?.shopify?.byStore, SHOPIFY_COMPARED_STORE);
  const au = getStore(sku?.shopify?.byStore, SHOPIFY_READONLY_STORE);
  const keg = String(sku?.containerType || '').toLowerCase() === 'keg';
  return {
    monday: blankId(sku?.mondayId),
    shopifyNz: !keg && shopifyStoreLinkMissing(nz),
    shopifyAu: !keg && shopifyStoreLinkMissing(au),
    cin7: blankId(sku?.cin7Sku),
    kounta: !sku?.kounta?.linked,
  };
}

const PLATFORM_LINK_LABELS = [
  ['monday', 'Monday'],
  ['shopifyNz', 'Shopify NZ'],
  ['shopifyAu', 'Shopify AU'],
  ['cin7', 'Cin7'],
  ['kounta', 'Kounta'],
];

/** Platform names with no link on this SKU. */
export function missingPlatformLabels(sku) {
  const missing = skuPlatformLinkMissing(sku);
  return PLATFORM_LINK_LABELS.filter(([key]) => missing[key]).map(([, label]) => label);
}

/**
 * SKU is missing a platform link.
 * system 'shopify' matches NZ or AU; 'all' / omitted matches any platform.
 */
export function skuHasMissingPlatformLink(sku, system = 'all') {
  const missing = skuPlatformLinkMissing(sku);
  if (system === 'monday') return missing.monday;
  if (system === 'cin7') return missing.cin7;
  if (system === 'kounta') return missing.kounta;
  if (system === 'shopify') return missing.shopifyNz || missing.shopifyAu;
  return PLATFORM_LINK_LABELS.some(([key]) => missing[key]);
}

/** Table / drawer title. Empty name + real id is not "unassigned". */
export function beerDisplayName(beer) {
  if (beer?.productName) return beer.productName;
  if (beer?.productMatrixId == null || beer.productMatrixId === '') return 'Unassigned SKUs';
  return `Beer #${beer.productMatrixId}`;
}

/** Any SKU in the beer linked to the given system. */
export function beerSystemLinked(beer, system) {
  return (beer?.skus || []).some((sku) => skuSystemLinked(sku)[system]);
}

/**
 * Fallback grouping when the backend only returns the flat `results.skus` list
 * (older scan payloads). Mirrors the Step 4 beer shape closely enough for the table.
 */
export function groupSkusIntoBeers(skus) {
  const byId = new Map();
  (skus || []).forEach((sku) => {
    const id = sku.productMatrixId ?? null;
    const key = id == null ? '__unassigned__' : String(id);
    if (!byId.has(key)) {
      byId.set(key, {
        productMatrixId: id,
        productName: id == null ? 'Unassigned SKUs' : (sku.productName || ''),
        productAbv: sku.productAbv ?? null,
        productType: sku.productType ?? null,
        productRange: sku.productRange ?? null,
        shopify: null,
        skus: [],
        counts: {
          guide: 0, monday: 0, cin7: 0, shopify: 0, kounta: 0, total: 0, missing: 0, misalignment: 0,
        },
        formats: [],
      });
    }
    const beer = byId.get(key);
    beer.skus.push(sku);
    const c = skuSystemCounts(sku);
    SYSTEMS.forEach((s) => { beer.counts[s] += c[s]; });
    beer.counts.total += sku.totalDiscrepancies || 0;
    beer.counts.missing += sku.issueCounts?.missing || 0;
    beer.counts.misalignment += sku.issueCounts?.misalignment || 0;
    beer.formats.push(skuFormatLabel(sku));
    if (!beer.shopify && sku.shopify?.byStore) beer.shopify = sku.shopify.byStore;
  });
  return [...byId.values()].sort((a, b) => b.counts.total - a.counts.total);
}

export function stopRowClick(e) {
  e.stopPropagation();
}

/** Matrix product edit page (same origin). */
export function matrixProductHref(productMatrixId) {
  if (productMatrixId == null || productMatrixId === '') return null;
  return `/dashboard/product-database/products/${productMatrixId}/edit`;
}

export function mondaySkuPulseHref(mondayId) {
  if (mondayId == null || mondayId === '') return null;
  return `https://garageproject.monday.com/boards/${MONDAY_SKU_DATABASE_BOARD_ID}/pulses/${mondayId}`;
}

export function cin7ProductHref(dearCode) {
  if (dearCode == null || dearCode === '') return null;
  return `https://inventory.dearsystems.com/Product#${dearCode}`;
}

export function kountaProductHref(kountaProductId) {
  if (kountaProductId == null || kountaProductId === '') return null;
  return `https://my.kounta.com/product/update?sub=productinfo&id=${kountaProductId}`;
}

export function shopifyProductAdminHref(store, shopifyProductId) {
  if (shopifyProductId == null || shopifyProductId === '') return null;
  const slug = store === SHOPIFY_READONLY_STORE ? 'garageproject-australia' : 'garage-project';
  return `https://${slug}.myshopify.com/admin/products/${shopifyProductId}`;
}

export function errorMessage(err, fallback) {
  return String(err?.response?.data?.error || err?.message || err?.error || fallback);
}
